import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Form, Row, Col, Badge, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

function ActivityLogs({ accessToken }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    actionType: '',
    resourceType: '',
    status: ''
  });

  useEffect(() => {
    fetchActivities();
  }, [accessToken]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${BACKEND_URL}/activities`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
      setError(error.response?.data?.error || 'Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variant = status === 'success' ? 'success' : 'danger';
    return <Badge bg={variant}>{status}</Badge>;
  };

  const getActionBadge = (action) => {
    const variants = {
      create: 'primary',
      delete: 'danger',
      update: 'warning',
      view: 'info',
      add: 'success',
      remove: 'danger',
      search: 'info'
    };
    return <Badge bg={variants[action] || 'secondary'}>{action}</Badge>;
  };

  const getResourceBadge = (resource) => {
    const variants = {
      branch: 'info',
      repository: 'primary',
      collaborator: 'warning',
      commits: 'success',
      branches: 'secondary'
    };
    return <Badge bg={variants[resource] || 'light'}>{resource}</Badge>;
  };

  const filteredActivities = activities.filter(activity => {
    return (
      (!filters.actionType || activity.action_type === filters.actionType) &&
      (!filters.resourceType || activity.resource_type === filters.resourceType) &&
      (!filters.status || activity.status === filters.status)
    );
  });

  const uniqueActionTypes = [...new Set(activities.map(a => a.action_type))];
  const uniqueResourceTypes = [...new Set(activities.map(a => a.resource_type))];
  const uniqueStatuses = [...new Set(activities.map(a => a.status))];

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Card>
        <Card.Header>
          <h2>Activity Logs</h2>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Action Type</Form.Label>
                <Form.Select
                  value={filters.actionType}
                  onChange={(e) => setFilters({ ...filters, actionType: e.target.value })}
                >
                  <option value="">All Actions</option>
                  {uniqueActionTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Resource Type</Form.Label>
                <Form.Select
                  value={filters.resourceType}
                  onChange={(e) => setFilters({ ...filters, resourceType: e.target.value })}
                >
                  <option value="">All Resources</option>
                  {uniqueResourceTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">All Statuses</option>
                  {uniqueStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {filteredActivities.length === 0 ? (
            <Alert variant="info">No activities found matching the selected filters.</Alert>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Resource Type</th>
                  <th>Resource Name</th>
                  <th>Status</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredActivities.map((activity, index) => (
                  <tr key={index}>
                    <td>{format(new Date(activity.timestamp), 'MMM d, yyyy HH:mm:ss')}</td>
                    <td>
                      <Badge bg="info">
                        {activity.username || 'Unknown'}
                      </Badge>
                    </td>
                    <td>{getActionBadge(activity.action_type)}</td>
                    <td>{getResourceBadge(activity.resource_type)}</td>
                    <td>{activity.resource_name || '-'}</td>
                    <td>{getStatusBadge(activity.status)}</td>
                    <td>
                      {activity.details ? (
                        <pre style={{ fontSize: '0.8em', margin: 0 }}>
                          {JSON.stringify(activity.details, null, 2)}
                        </pre>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default ActivityLogs;
